import logoWuDark from '@assets/logo-wu-dark.svg';
import { LogoutIcon } from '@components/icons/LogoutIcon';
import { getExternalLinks } from '@config/appConfig';
import { useGlobalDIContext } from '@context/GlobalDIContext';
import { TRANSACTION_ROUTES } from '@features/transaction/routes';
import { USER_ROUTES } from '@features/user/routes';
import { useCheckAuth } from '@features/user/states';
import { useGetClientPermission } from '@features/user/states/queries/useGetClientPermission';
import { Iconify } from '@g2-shared/components';
import { WuLayoutHeaderMenuDropdown } from '@layout/components/WuLayoutHeaderMenuDropdown';
import WuLayoutMobileMenuLang from '@layout/components/WuLayoutMobileMenuLang';
import WuLayoutMobileMenuUserCard from '@layout/components/WuLayoutMobileMenuUserCard';
import { layoutStyle } from '@layout/styles';
import { Button, Divider, Link, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import i18n from 'i18next';

const { VITE_APP_LOGOUT_URL } = import.meta.env;

function WuLayoutMobileMenu() {
  const EXTERNAL_LINKS = getExternalLinks(i18n.language);

  const [open, setOpen] = useState<boolean>(false);
  const { isAuthenticated } = useCheckAuth();
  const { hasSubService } = useGetClientPermission(!!isAuthenticated);
  const { TranslationAdapter } = useGlobalDIContext();
  const { t } = TranslationAdapter();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const htmlEl = document.querySelector('html');
    const bodyEl = document.querySelector('body');
    const overflow = open ? 'hidden' : 'auto';
    if (htmlEl) htmlEl.style.overflow = overflow;
    if (bodyEl) bodyEl.style.overflow = overflow;
  }, [open]);

  return (
    <>
      <Button
        size="small"
        color="warning"
        sx={layoutStyle.layoutMobileMenuButton}
        onClick={() => setOpen(!open)}
      >
        <Iconify icon="material-symbols:menu" sx={{ width: 30, height: 30 }} />
      </Button>
      <Box
        sx={{
          ...layoutStyle.mobileMenuWrapper,
          display: open ? 'block' : 'none',
        }}
      >
        <Box sx={{ ...layoutStyle.mobileMenu, width: !open ? 0 : '100%' }}>
          <Box>
            <Box sx={layoutStyle.mobileMenuHeader}>
              <Box component="img" src={logoWuDark} width={200} />
              <Button sx={layoutStyle.mobileMenuCloseButton} onClick={() => setOpen(!open)}>
                <Iconify
                  icon="ic:baseline-close"
                  sx={{ color: '#000000', width: 30, height: 30 }}
                />
              </Button>
            </Box>
            {isAuthenticated && hasSubService('WUD') && <WuLayoutMobileMenuUserCard />}
            <Box sx={layoutStyle.mobileMenuItemsWrapper}>
              {isAuthenticated && hasSubService('WUD') && (
                <>
                  <RouterLink to={TRANSACTION_ROUTES.transactionHistory}>{t('history')}</RouterLink>
                  <RouterLink to={USER_ROUTES.userDetails}>{t('profile')}</RouterLink>
                  <Divider />
                  <Link href={TRANSACTION_ROUTES.sendMoney}>{t('sendMoney')}</Link>
                </>
              )}
              <Link href={EXTERNAL_LINKS.trackTransaction}>{t('trackTransaction')}</Link>
              <Divider />
              <Button
                href="#"
                id="main-menu-button"
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                sx={{ alignItems: 'center', gap: 1 }}
              >
                {t('menu')}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.00028 10.3343C7.84451 10.3346 7.69355 10.2803 7.57361 10.1809L3.57361 6.8476C3.29011 6.61196 3.25131 6.1911 3.48695 5.9076C3.72259 5.62409 4.14344 5.58529 4.42695 5.82093L8.00028 8.8076L11.5736 5.9276C11.7113 5.81575 11.888 5.76342 12.0644 5.78219C12.2408 5.80096 12.4025 5.88928 12.5136 6.0276C12.637 6.16617 12.6971 6.34992 12.6794 6.53464C12.6617 6.71936 12.5678 6.88834 12.4203 7.00093L8.42028 10.2209C8.29689 10.3046 8.14901 10.3445 8.00028 10.3343Z"
                    fill="#212B36"
                  />
                </svg>
              </Button>
              <WuLayoutHeaderMenuDropdown
                id="main-menu"
                anchorEl={anchorEl}
                open={anchorEl !== null}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'profile-menu-button',
                }}
              >
                {isAuthenticated && hasSubService('WUD') && (
                  <RouterLink
                    style={layoutStyle.menuDropdownLink}
                    to={TRANSACTION_ROUTES.sendMoney}
                  >
                    <MenuItem sx={{ fontSize: 15 }} onClick={handleClose}>
                      {t('sendMoney')}
                    </MenuItem>
                  </RouterLink>
                )}
                <RouterLink
                  style={layoutStyle.menuDropdownLink}
                  to={EXTERNAL_LINKS.trackTransaction}
                >
                  <MenuItem sx={{ fontSize: 15 }} onClick={handleClose}>
                    {t('trackTransaction')}
                  </MenuItem>
                </RouterLink>
                <Link sx={layoutStyle.menuDropdownLink} href={EXTERNAL_LINKS.findLocation}>
                  <MenuItem sx={{ fontSize: 15 }} onClick={handleClose}>
                    {t('findLocation')}
                  </MenuItem>
                </Link>
                <Link
                  sx={layoutStyle.menuDropdownLink}
                  href={EXTERNAL_LINKS.findLocationForVerification}
                >
                  <MenuItem sx={{ fontSize: 15 }} onClick={handleClose}>
                    {t('findLocationForVerification')}
                  </MenuItem>
                </Link>
                <MenuItem sx={layoutStyle.menuDropdownLink} onClick={handleClose}>
                  {t('customerSupport')}
                </MenuItem>
              </WuLayoutHeaderMenuDropdown>
            </Box>
          </Box>
          <Box>
            <WuLayoutMobileMenuLang />
            {isAuthenticated && (
              <Link href={VITE_APP_LOGOUT_URL}>
                <Button
                  sx={{
                    width: '100%',
                    mt: 3,
                    color: '#212B36',
                    justifyContent: 'start',
                    gap: 2.5,
                    fontWeight: 300,
                  }}
                >
                  <Box sx={{ mx: 1, pt: 1 }}>
                    <LogoutIcon />
                  </Box>
                  {t('logout')}
                </Button>
              </Link>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default WuLayoutMobileMenu;
